// ─── ArtefactoToolbarActions — botones cross-artefacto (0637 + 0638) ─────────
// Componente shared que agrupa las 3 acciones canónicas del visor de un
// artefacto: exportar PNG, exportar PDF, pantalla completa.
//
// Cada acción es opcional — si su callback es null/undefined no se renderiza
// (permite a docs y flujogramas usar el mismo componente con subset).
//
// 0638: API cambiada de `iconPath: string` a `icon: ReactNode`. Permite SVGs
// con multiples paths + <text> inline (icono PDF/PNG con label dentro estilo
// Adobe — reconocible al instante en modo icon-only).
//
// UX:
// - Por defecto compact=true → cuadrados 32×32 con tooltip nativo (title).
// - compact=false → icono + label en fila.
// - Loading state opcional por acción (spinner simple).
// - Pantalla completa: icono cambia entre maximize/minimize según isFullscreen.

import { useState, type ReactNode } from 'react';
import { C } from '../../../lib/design';

export interface ArtefactoToolbarActionsProps {
  /** Si null, no se renderiza el botón. */
  onExportPng?: (() => void | Promise<void>) | null;
  /** Si null, no se renderiza el botón. */
  onExportPdf?: (() => void | Promise<void>) | null;
  /** Si null, no se renderiza el botón. */
  onFullscreen?: (() => void) | null;
  /** Estado actual de pantalla completa — alterna icono y tooltip. */
  isFullscreen?: boolean;
  /** Si true (default), solo icono (sin label debajo). */
  compact?: boolean;
  /** i18n labels — fallback español si no se proveen. */
  labels?: {
    png?: string;
    pdf?: string;
    fullscreen?: string;
    exitFullscreen?: string;
    exporting?: string;
  };
}

// =============================================================================
// Iconos SVG inline (cumplen .claude/rules/frontend.md — premium, no librerías)
// =============================================================================

// PNG: badge tipo imagen con texto "PNG" inline (estilo Adobe).
const IconPng = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Marco del documento de imagen */}
    <rect x="3" y="3" width="18" height="18" rx="2" />
    {/* Sol/punto en esquina */}
    <circle cx="8" cy="8.5" r="1.3" fill="currentColor" stroke="none" />
    {/* Montaña indicando imagen */}
    <path d="M21 16l-5-5-4 4-2-2-7 7" />
    {/* Texto "PNG" inline */}
    <text
      x="12"
      y="20.5"
      fontFamily="DM Sans, sans-serif"
      fontSize="3.6"
      fontWeight="800"
      textAnchor="middle"
      fill="currentColor"
      stroke="none"
      letterSpacing="0.3"
    >
      PNG
    </text>
  </svg>
);

// PDF: badge tipo documento con texto "PDF" inline (estilo Adobe).
const IconPdf = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Marco del documento con esquina doblada */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    {/* Texto "PDF" inline */}
    <text
      x="12"
      y="17.5"
      fontFamily="DM Sans, sans-serif"
      fontSize="5"
      fontWeight="800"
      textAnchor="middle"
      fill="currentColor"
      stroke="none"
      letterSpacing="0.4"
    >
      PDF
    </text>
  </svg>
);

// Pantalla completa (entrar)
const IconFullscreen = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
    <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
    <path d="M3 16v3a2 2 0 0 0 2 2h3" />
    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
  </svg>
);

// Pantalla completa (salir)
const IconMinimize = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 14h6v6" />
    <path d="M20 10h-6V4" />
    <path d="M14 10l7-7" />
    <path d="M3 21l7-7" />
  </svg>
);

// =============================================================================
// Botón individual
// =============================================================================

interface SingleActionButtonProps {
  icon: ReactNode;
  label: string;
  title: string;
  onClick: () => void | Promise<void>;
  compact: boolean;
  loadingLabel: string;
}

function SingleActionButton({
  icon,
  label,
  title,
  onClick,
  compact,
  loadingLabel,
}: SingleActionButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await Promise.resolve(onClick());
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={loading ? loadingLabel : title}
      aria-label={label}
      disabled={loading}
      style={{
        // 0637b: modo icon-only por defecto (compact=true). Cuadrado 32×32 — el
        // texto vive en tooltip nativo (title). Más limpio y ahorra ancho de
        // toolbar. compact=false renderiza icono+texto fila (mantenido para
        // casos donde el caller quiera label visible).
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: compact ? 0 : 6,
        padding: compact ? 0 : '6px 10px',
        width: compact ? 32 : undefined,
        height: compact ? 32 : undefined,
        background: '#fff',
        color: C.azul1,
        border: '1px solid #D1D5DB',
        borderRadius: 6,
        cursor: loading ? 'wait' : 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 11,
        fontWeight: 600,
        transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
        opacity: loading ? 0.6 : 1,
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          const el = e.currentTarget as HTMLElement;
          el.style.background = '#F0F4FF';
          el.style.borderColor = C.azul2;
          el.style.color = C.azul2;
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          const el = e.currentTarget as HTMLElement;
          el.style.background = '#fff';
          el.style.borderColor = '#D1D5DB';
          el.style.color = C.azul1;
        }
      }}
    >
      {icon}
      {!compact && <span style={{ whiteSpace: 'nowrap' }}>{loading ? loadingLabel : label}</span>}
    </button>
  );
}

// =============================================================================
// Componente público
// =============================================================================

export function ArtefactoToolbarActions({
  onExportPng,
  onExportPdf,
  onFullscreen,
  isFullscreen,
  compact = true,
  labels,
}: ArtefactoToolbarActionsProps) {
  const lbl = {
    png: labels?.png ?? 'PNG',
    pdf: labels?.pdf ?? 'PDF',
    fullscreen: labels?.fullscreen ?? 'Pantalla completa',
    exitFullscreen: labels?.exitFullscreen ?? 'Salir pantalla completa',
    exporting: labels?.exporting ?? 'Exportando…',
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {onExportPng && (
        <SingleActionButton
          icon={<IconPng />}
          label={lbl.png}
          title={`Exportar como ${lbl.png}`}
          onClick={onExportPng}
          compact={compact}
          loadingLabel={lbl.exporting}
        />
      )}
      {onExportPdf && (
        <SingleActionButton
          icon={<IconPdf />}
          label={lbl.pdf}
          title={`Exportar como ${lbl.pdf}`}
          onClick={onExportPdf}
          compact={compact}
          loadingLabel={lbl.exporting}
        />
      )}
      {onFullscreen && (
        <SingleActionButton
          icon={isFullscreen ? <IconMinimize /> : <IconFullscreen />}
          label={isFullscreen ? lbl.exitFullscreen : lbl.fullscreen}
          title={isFullscreen ? lbl.exitFullscreen : lbl.fullscreen}
          onClick={onFullscreen}
          compact={compact}
          loadingLabel={lbl.exporting}
        />
      )}
    </div>
  );
}
